import { Document } from "models/schemas";
import { FileMetadata } from "../input-files/definitions";
import { getFileExtension } from "utils/date-format";

/**
 * Converts File or FileMetadata items to temporary Document objects for collision checking
 * @param items Array of File or FileMetadata objects to convert
 * @param parentId The parent ID for the documents
 * @param teamName The team name for the documents
 * @param defaultDocumentType Optional default document type
 * @returns Array of temporary Document objects
 */
export const convertItemsToTempDocuments = (
  items: (File | FileMetadata)[],
  parentId: string,
  teamName: string,
  defaultDocumentType?: string
): Document[] => {
  return items.map((item) => {
    let fileName: string;
    let extentionType: string;
    let documentType: string;

    if ("file" in item) {
      // FileMetadata
      fileName =
        item.fileName ||
        item.file.name.substring(0, item.file.name.lastIndexOf("."));
      extentionType = item.file.name.substring(item.file.name.lastIndexOf("."));
      documentType = item.documentType || defaultDocumentType || "";
    } else {
      // File
      const lastDotIndex = item.name.lastIndexOf(".");
      fileName =
        lastDotIndex > -1 ? item.name.substring(0, lastDotIndex) : item.name;
      extentionType =
        lastDotIndex > -1 ? item.name.substring(lastDotIndex) : "";
      documentType = defaultDocumentType || "";
    }

    return {
      documentId: `temp-${crypto.randomUUID()}`,
      fileName,
      extentionType,
      parentId,
      documentType,
      teamName,
      securityLevel: "",
      deleted: false,
      createdAt: Date.now().toString(), // for proper sorting in collision detection
    };
  });
};

/**
 * Converts stagedFiles to Document objects, using current fileName (which may be renamed)
 * @param stagedFiles Array of StagedFile objects
 * @param parentId The parent ID for the documents
 * @param teamName The team name for the documents
 * @returns Array of Document objects
 */
export const convertStagedFilesToDocuments = (
  stagedFiles: any[],
  parentId: string,
  teamName: string
): Document[] => {
  return stagedFiles
    .filter((sf) => !sf.toDelete && !sf.isNew)
    .map((sf) => {
      if (sf.originalDocument) {
        return {
          ...sf.originalDocument,
          fileName: sf.fileName,
        };
      } else if (sf.file) {
        const extentionType = getFileExtension(sf.file.name);
        return {
          documentId: `staged-${sf.id}`,
          fileName: sf.fileName,
          extentionType,
          parentId,
          documentType: sf.documentType,
          teamName,
          securityLevel: "",
          deleted: false,
          createdAt: Date.now(),
        };
      }
      return null;
    })
    .filter((doc): doc is Document => doc !== null);
};
